/**
 * Jovii — IS THE PAGE STILL AT ITS TOP?
 *
 * One boolean for the whole shell: `scrolled.away` is true once the reader has
 * left the top of the page. The top chrome reads it to swap between its two
 * registers — the full liquid-glass pills at rest, and the much thinner
 * see-through ones once artwork is scrolling underneath (the owner: "still
 * there, still clickable, but much more transparent"). See the away blocks in
 * `TopBar.svelte` and `BubbleNav.svelte`, and the `--nav-*-away` tokens in
 * `app.css`.
 *
 * WHY A SCROLL LISTENER AND NOT AN IntersectionObserver. An observer needs a
 * sentinel element parked at the top of the document flow, and the top of the
 * document is the one piece of ground this shell does not own — the bar itself
 * occupies it (sticky, ≥768px) and every route decides for itself what comes
 * first. A listener owns no DOM and asks one number. The cost profile is the
 * same either way because of what the handler DOESN'T do:
 *
 *   • it is `passive`, so it never blocks the compositor's scroll;
 *   • it reads `scrollY` INSIDE the event, where layout is clean by
 *     construction — a scroll event fires from the rendering steps, before
 *     any rAF callback has written a class. This used to defer the read to
 *     its own rAF, which sounds thriftier and measured worse: by the time
 *     that rAF ran, the focus tracker's rAF had already toggled classes for
 *     the frame, and the one "cheap" scrollY read became a forced
 *     style-and-layout pass of the whole page — 741ms of a single throttled
 *     fling down Home (docs/PERF-SCROLL.md). Same number of reads either
 *     way; only the timing made one of them free;
 *   • it writes ONE boolean, and only when the answer actually changes —
 *     Svelte ignores same-value writes, so a thousand scroll events between
 *     the two thresholds reach zero subscribers.
 *
 * The consumer flips one class on one element and CSS does all the drawing.
 * Nothing here styles anything, and nothing here runs per-frame style writes.
 *
 * TWO THRESHOLDS, NOT ONE. Enter at 32px, leave at 8px. A single number is a
 * knife edge: iOS momentum settling on exactly it, or a d-pad correction
 * nudging 2px, would flap the whole bar's crossfade. The 24px between the two
 * is dead band, and both are small enough that "I have scrolled" is answered
 * before the first rail reaches the pills.
 *
 * A TELEVISION NEVER TRACKS. The TV tier kills backdrop-filter and collapses
 * transitions for frame rate (`html[data-tv='on']` in app.css), so a thinned
 * fill there would be a see-through pill with NO blur behind it — illegible
 * glass over artwork, which is exactly the trade the nav tokens were measured
 * to avoid. `applyTv()` writes the flag before the app mounts and never
 * changes its mind, so one check at start is enough: on a TV this module
 * installs nothing and `away` stays false for the life of the tab.
 * (`app.css` re-pins the `--nav-*-away` tokens to the resting set under
 * `data-tv` as a second lock.)
 */

/** The page has scrolled past the top: the chrome should get out of the way. */
const ENTER = 32;
/** …and back under this it is "at the top" again. The gap is hysteresis. */
const LEAVE = 8;

let away = $state(false);

/** Live scroll state. Read `scrolled.away` in a component and it stays reactive. */
export const scrolled: { readonly away: boolean } = {
  get away() {
    return away;
  }
};

let trackers = 0;

function measure(): void {
  if (trackers === 0) return;
  const y = window.scrollY;
  // Two thresholds; between them the previous answer stands. Same-value
  // writes are free, so no guard beyond the hysteresis itself is needed.
  if (y > ENTER) away = true;
  else if (y < LEAVE) away = false;
}

// The read happens in the event on purpose — see the header. Layout is clean
// here; a deferred read lands after the frame's class writes and pays for
// them.
const onScroll = measure;

/**
 * Start answering. Called from an `$effect` by whichever bar is mounted; the
 * teardown is refcounted because the two bars trade places when the viewport
 * crosses 768px — the arriving bar must not lose the listener the departing
 * bar is about to release. When nobody listens (the watch screen), nothing runs.
 */
export function trackScrolled(): () => void {
  if (typeof window === 'undefined') return () => {};
  // A television keeps its opaque-ish chrome; see the header.
  if (document.documentElement.dataset.tv === 'on') return () => {};

  trackers += 1;
  if (trackers === 1) {
    window.addEventListener('scroll', onScroll, { passive: true });
    // The tab may arrive mid-page (a restored scroll position, a #fragment):
    // answer immediately rather than waiting for the first scroll event.
    measure();
  }

  return () => {
    trackers -= 1;
    if (trackers === 0) {
      window.removeEventListener('scroll', onScroll);
      away = false;
    }
  };
}
